/**
 * ArquivosTab (#180) — aba "Arquivos" do workspace de projeto.
 * Upload via `POST /uploads?folder=projects` (S3) → grava metadados em
 * `project.attachments[]` (PUT no projeto). Lista com download + remover.
 */

import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { useProjectById, useUpdateProject } from '../hooks';
import type { ProjectAttachment } from '../types';

function newId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `att-${Date.now()}-${Math.round(performance.now())}`;
}

function fmtSize(bytes?: number): string {
  if (!bytes) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function fmtMeta(att: ProjectAttachment): string {
  const parts: string[] = [];
  const ext = att.name.includes('.') ? att.name.split('.').pop()?.toUpperCase() : undefined;
  if (ext) parts.push(ext);
  const size = fmtSize(att.size);
  if (size) parts.push(size);
  if (att.uploadedByName) parts.push(att.uploadedByName);
  return parts.join(' · ');
}

export interface ArquivosTabProps {
  projectId: string;
}

export const ArquivosTab: React.FC<ArquivosTabProps> = ({ projectId }) => {
  const { data: project } = useProjectById(projectId);
  const updateProject = useUpdateProject();
  const { state } = useAuth();
  const user = state.user;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const attachments = project?.attachments ?? [];

  const handleFiles = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0 || !project) return;
    setUploading(true);
    try {
      const added: ProjectAttachment[] = [];
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append('file', file);
        const res = await api.post('uploads?folder=projects', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const data = res.data.data as { url: string; originalName?: string; mimeType?: string; size?: number };
        added.push({
          id: newId(),
          name: data.originalName || file.name,
          url: data.url,
          ...(data.mimeType ? { mimeType: data.mimeType } : {}),
          ...(typeof data.size === 'number' ? { size: data.size } : {}),
          ...(user?.id ? { uploadedById: user.id } : {}),
          ...(user ? { uploadedByName: `${user.firstName} ${user.lastName}`.trim() } : {}),
          uploadedAt: new Date().toISOString(),
        });
      }
      await updateProject.mutateAsync({ id: projectId, data: { attachments: [...attachments, ...added] } });
    } catch {
      toast.error('Erro ao enviar arquivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = async (id: string): Promise<void> => {
    if (!project) return;
    try {
      await updateProject.mutateAsync({ id: projectId, data: { attachments: attachments.filter((a) => a.id !== id) } });
    } catch {
      toast.error('Erro ao remover arquivo');
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6" data-testid="project-ws-arquivos">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
        data-testid="project-arquivos-input"
      />

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold" style={{ color: 'var(--theme-card-text)' }}>
          Arquivos {attachments.length > 0 ? `(${attachments.length})` : ''}
        </h2>
        <Button
          icon="upload"
          size="sm"
          loading={uploading}
          onClick={() => fileInputRef.current?.click()}
          data-testid="project-arquivos-upload"
        >
          Enviar arquivo
        </Button>
      </div>

      {attachments.length === 0 ? (
        <div
          className="rounded-lg border border-dashed p-10 text-center"
          style={{ borderColor: 'var(--theme-card-border)' }}
          data-testid="project-arquivos-empty"
        >
          <Icon name="file-text" className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--theme-muted-text)' }} />
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Nenhum arquivo ainda. Envie briefings, contratos e materiais do projeto.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {attachments.map((att) => (
            <div
              key={att.id}
              className="flex items-center gap-3 rounded-lg border px-3 py-2.5"
              style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
              data-testid={`project-arquivo-${att.id}`}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'var(--theme-primary-background)' }}
              >
                <Icon name="file-text" className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
                  {att.name}
                </div>
                <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                  {fmtMeta(att)}
                </div>
              </div>
              <a
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                download={att.name}
                className="inline-flex items-center gap-1.5 text-sm font-medium px-2.5 py-1.5 rounded-lg transition-colors flex-shrink-0"
                style={{ color: 'var(--theme-primary)' }}
                data-testid={`project-arquivo-download-${att.id}`}
              >
                <Icon name="download" className="w-4 h-4" />
                <span className="hidden sm:inline">Baixar</span>
              </a>
              <button
                type="button"
                onClick={() => void handleRemove(att.id)}
                className="p-1.5 rounded-lg transition-colors flex-shrink-0"
                style={{ color: 'var(--theme-muted-text)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                title="Remover arquivo"
                data-testid={`project-arquivo-remove-${att.id}`}
              >
                <Icon name="trash" className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

ArquivosTab.displayName = 'ArquivosTab';
